import { useState, useEffect } from "react";
import { useLang } from "@/hooks/use-lang";
import { COUNTRIES } from "@/data/countries";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, Loader2 } from "lucide-react";
import { externalSupabase } from "@/integrations/external-supabase/client";

interface CityPickerProps {
  onSelect: (city: string) => void;
  countryHint?: string;
}

// Build a French name → English name lookup
const frToEnMap: Record<string, string> = {};
for (const c of COUNTRIES) {
  frToEnMap[c.nameFr.toLowerCase()] = c.name;
}

function resolveCountryName(countryHint?: string): string | null {
  if (!countryHint) return null;
  const lower = countryHint.toLowerCase();

  // Direct match on English country names
  for (const c of COUNTRIES) {
    if (lower.includes(c.name.toLowerCase())) return c.name;
  }
  // French name lookup
  for (const [frName, enName] of Object.entries(frToEnMap)) {
    if (lower.includes(frName)) return enName;
  }
  return countryHint;
}

const CityPicker = ({ onSelect, countryHint }: CityPickerProps) => {
  const { lang } = useLang();
  const [showManual, setShowManual] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const country = resolveCountryName(countryHint);
    if (!country) {
      setCities([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    externalSupabase
      .from("cities")
      .select("city")
      .eq("country", country)
      .limit(5)
      .then(({ data, error }) => {
        if (error) {
          console.error("Error fetching cities:", error);
          setCities([]);
        } else {
          setCities((data ?? []).map((r) => r.city));
        }
        setLoading(false);
      });
  }, [countryHint]);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualInput.trim()) {
      onSelect(manualInput.trim());
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-xs mx-auto mt-4 flex items-center justify-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        {lang === "fr" ? "Chargement des villes..." : "Loading cities..."}
      </div>
    );
  }

  // If no cities found or manual mode, show text input directly
  if (showManual || cities.length === 0) {
    return (
      <div className="w-full max-w-xs mx-auto mt-2">
        <form onSubmit={handleManualSubmit} className="flex gap-2">
          <Input
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            placeholder={lang === "fr" ? "Nom de la ville..." : "City name..."}
            className="h-11 text-base flex-1"
            autoFocus
          />
          <Button type="submit" size="icon" disabled={!manualInput.trim()} className="h-11 w-11 shrink-0">
            <Send className="w-4 h-4" />
          </Button>
        </form>
        {cities.length > 0 && (
          <button
            onClick={() => setShowManual(false)}
            className="mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center"
          >
            ← {lang === "fr" ? "Retour aux suggestions" : "Back to suggestions"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-xs mx-auto mt-2">
      <p className="text-sm text-muted-foreground text-center mb-3">
        {lang === "fr" ? "Villes populaires" : "Popular cities"}
      </p>
      <div className="flex flex-col gap-2">
        {cities.map((city) => (
          <button
            key={city}
            onClick={() => onSelect(city)}
            className="w-full py-3 px-4 rounded-2xl bg-background/60 backdrop-blur-xl border border-border/30 shadow-md hover:bg-background/80 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.97] transition-all duration-200 text-foreground text-sm font-semibold text-center"
          >
            {city}
          </button>
        ))}
      </div>
      <p className="mt-4 text-xs text-muted-foreground text-center leading-relaxed">
        {lang === "fr"
          ? "Ces villes sont des suggestions. Vous pouvez aussi entrer une autre ville."
          : "These cities are suggestions. You can also search for another city."}
      </p>
      <button
        onClick={() => setShowManual(true)}
        className="mt-2 w-full py-2.5 px-4 rounded-xl bg-accent/60 hover:bg-accent/80 border border-border/30 text-foreground text-sm font-medium text-center transition-all duration-200 flex items-center justify-center gap-1.5"
      >
        🔍 {lang === "fr" ? "Chercher une autre ville" : "Search another city"}
      </button>
    </div>
  );
};

// Keep findCities export for any other usage, now as a no-op fallback
function findCities(_messages: string[], _countryHint?: string): string[] {
  return [];
}

export { findCities };
export default CityPicker;
